/*
  Stempelung korrigieren - nur für die Planung.

  "Erfasste Arbeitszeit": Speichern oder Löschen.
  Genehmigungen (onApprove gesetzt): Genehmigen mit den korrigierten Daten in
  einem Schritt, oder Ablehnen - die Entscheidung trifft die aufrufende Seite.
*/

import React, { useEffect, useState } from "react";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import { IconDelete, IconError, IconSuccess } from "../../design-system/icons";
import { deletePunchEntry, updatePunchEntry } from "./punchEntryApi";
import {
  endsNextDay,
  punchFormFromEntry,
  punchPayloadFromForm,
  validatePunchForm,
} from "./punchEntryForm";

/*
  entry: { id, employeeName, checkIn, checkOut, breakMinutes } oder null (geschlossen).
  onChanged wird nach Speichern oder Löschen aufgerufen ("saved" | "deleted").
  onApprove(payload) / onReject(): Genehmigungs-Modus; onApprove liefert ein
  Promise und speichert die Korrektur zusammen mit der Genehmigung.
*/
export default function PunchEntryDialog({ entry, onClose, onChanged, onApprove, onReject }) {
  const isApproval = typeof onApprove === "function";
  const [form, setForm] = useState(() => punchFormFromEntry());
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setForm(punchFormFromEntry(entry.checkIn, entry.checkOut, entry.breakMinutes));
    setFormError("");
    setConfirmDelete(false);
  }, [entry]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async () => {
    const validationError = validatePunchForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      await updatePunchEntry(entry.id, punchPayloadFromForm(form));
      onChanged?.("saved");
      onClose?.();
    } catch (requestError) {
      setFormError(requestError?.response?.data?.message || "Die Stempelung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    const validationError = validatePunchForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      await onApprove(punchPayloadFromForm(form));
      onClose?.();
    } catch (requestError) {
      setFormError(requestError?.response?.data?.message || "Die Anfrage konnte nicht genehmigt werden.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await deletePunchEntry(entry.id);
      onChanged?.("deleted");
      onClose?.();
    } catch (requestError) {
      setFormError(requestError?.response?.data?.message || "Die Stempelung konnte nicht gelöscht werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AppDialog open={Boolean(entry) && !confirmDelete} onClose={busy ? undefined : onClose} size="sm">
        <AppDialogHeader
          title={isApproval ? "Stempelung prüfen" : "Stempelung bearbeiten"}
          subtitle={entry?.employeeName || ""}
        />
        <AppDialogBody>
          <AppFieldStack>
            {formError ? <AppCallout variant="error" description={formError} /> : null}
            <AppField label="Datum" required>
              <AppInput type="date" value={form.date} onChange={update("date")} aria-label="Datum" />
            </AppField>
            <AppField label="Beginn" required>
              <AppInput type="time" value={form.start} onChange={update("start")} aria-label="Beginn" />
            </AppField>
            <AppField
              label="Ende"
              help={endsNextDay(form) ? "Endet am Folgetag." : "Leer lassen, wenn noch eingestempelt."}
            >
              <AppInput type="time" value={form.end} onChange={update("end")} aria-label="Ende" />
            </AppField>
            <AppField label="Pause (Minuten)">
              <AppInput
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={form.breakMinutes}
                onChange={update("breakMinutes")}
                aria-label="Pause (Minuten)"
              />
            </AppField>
          </AppFieldStack>
        </AppDialogBody>
        {isApproval ? (
          <AppDialogFooter
            startContent={(
              <AppButton variant="error" onClick={() => onReject?.()} disabled={busy}>
                <IconError />
                Ablehnen
              </AppButton>
            )}
          >
            <AppButton variant="neutral" onClick={onClose} disabled={busy}>Abbrechen</AppButton>
            <AppButton variant="primary" onClick={approve} disabled={busy}>
              <IconSuccess />
              Genehmigen
            </AppButton>
          </AppDialogFooter>
        ) : (
          <AppDialogFooter
            startContent={(
              <AppButton variant="error" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <IconDelete />
                Löschen
              </AppButton>
            )}
          >
            <AppButton variant="neutral" onClick={onClose} disabled={busy}>Abbrechen</AppButton>
            <AppButton variant="primary" onClick={save} disabled={busy}>Speichern</AppButton>
          </AppDialogFooter>
        )}
      </AppDialog>

      <AppConfirmDialog
        open={Boolean(entry) && confirmDelete}
        size="sm"
        title="Stempelung löschen?"
        description="Die Stempelung wird endgültig gelöscht. Eine offene Genehmigung dazu wird damit erledigt."
        confirmLabel="Löschen"
        confirmIcon={<IconDelete />}
        confirmTone="error"
        cancelLabel="Abbrechen"
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
